import useIsDayZeroUser from '@gaia/hooks/useIsDayZeroUser';
import usePersistentState from '@gaia/hooks/usePersistentState';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {useUrlProps} from '@map/Map/useUrlProps';
import {Dialog, DialogContent} from '@mui/material';
import {useEffect, useState} from 'react';

import {useUpsellModal} from './store';
import {UpsellCarousel} from './UpsellCarousel';

export default function UpsellModal() {
    const [upsellModalState, setUpsellModalState] = useUpsellModal();
    const [showModal, setShowModal] = useState(false);
    const {isAuthenticated, hasPremiumSubscription} = useUser();
    const isDayZeroUser = useIsDayZeroUser();
    const [hasSeenOnboardingUpsell, setHasSeenOnboardingUpsell] = usePersistentState<boolean>(
        `hasSeenOnboardingUpsell`,
        false
    );
    const urlProps = useUrlProps();

    // Only authenticated, Day 0, free-account users, who haven't already seen the onboarding upsell
    // should see the onboarding upsell.
    const shouldShowOnboardingUpsell =
        isAuthenticated &&
        !hasPremiumSubscription &&
        isDayZeroUser &&
        !hasSeenOnboardingUpsell &&
        urlProps.partner !== 'bdr';

    useEffect(() => {
        setShowModal(Boolean(upsellModalState));
        if (!upsellModalState) return;
        if (upsellModalState.selectedScreen) {
            // On component mount, fire the Encounter Paywall event (if this is a paywall carousel and not an onboarding upsell)
            ampli.encounterPaywall(upsellModalState.analyticsProperties);
        } else {
            ampli.encounterOnboardingUpsell();
        }
    }, [upsellModalState]);

    // If a user makes an account on gaiagps.com and is redirected to the map
    // this ensures they enter the onboarding upsell AB test.
    useEffect(() => {
        if (!shouldShowOnboardingUpsell) return;
        setTimeout(() => {
            setUpsellModalState({selectedScreen: undefined});
            setShowModal(true);
        }, 5000);
    }, [setUpsellModalState, shouldShowOnboardingUpsell]);

    const upsellModal = (
        <Dialog
            open={showModal}
            onClose={() => {
                setUpsellModalState(undefined);
                if (!upsellModalState?.selectedScreen) {
                    setHasSeenOnboardingUpsell(true);
                }
            }}
        >
            <DialogContent style={{padding: theme.spacing(2)}}>
                <UpsellCarousel
                    selectedScreen={upsellModalState?.selectedScreen}
                    onClose={() => {
                        setUpsellModalState(undefined);
                        if (!upsellModalState?.selectedScreen) {
                            setHasSeenOnboardingUpsell(true);
                        }
                    }}
                />
            </DialogContent>
        </Dialog>
    );

    if (upsellModalState?.selectedScreen) {
        // Contextual Paywalls are always shown
        return upsellModal;
    } else if (shouldShowOnboardingUpsell) {
        return upsellModal;
    } else {
        return null;
    }
}
